/**
 * Protocol-v1 primitives for the SDK: base64url, JCS canonical JSON (integers-only profile),
 * SHA-256, Ed25519 (audited @noble libraries, strict RFC 8032 verification to match the
 * Rust core's verify_strict), compact JWS and the signed-request base string.
 * Cross-language test vectors: packages/protocol/fixtures/signature-vectors.json.
 */
import * as ed from '@noble/ed25519';
import { sha256 as nobleSha256, sha512 } from '@noble/hashes/sha2.js';
import { operationHashInput, PROTOCOL_VERSION, REQUEST_SIG_VERSION } from '@pryme/protocol';
ed.hashes.sha512 = sha512;
const enc = new TextEncoder();
const dec = new TextDecoder();
export const b64url = {
    encode(bytes) {
        let s = '';
        for (const b of bytes)
            s += String.fromCharCode(b);
        return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    },
    decode(s) {
        if (!/^[A-Za-z0-9_-]*$/.test(s))
            throw new Error('invalid base64url');
        const pad = s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4);
        const bin = atob(pad);
        const out = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++)
            out[i] = bin.charCodeAt(i);
        return out;
    },
    /**
     * Is `s` the one canonical base64url encoding of its bytes? Lenient decoders also accept variants that
     * differ only in unused trailing bits; signatures must not have such twins (security review L15).
     */
    isCanonical(s) {
        try {
            return b64url.encode(b64url.decode(s)) === s;
        }
        catch {
            return false;
        }
    },
};
export const utf8 = (s) => enc.encode(s);
export function sha256(data) {
    return nobleSha256(typeof data === 'string' ? enc.encode(data) : data);
}
export const sha256b64 = (data) => b64url.encode(sha256(data));
/**
 * The operation hash Pryme binds approvals and decisions to (docs/PROTOCOL.md, security review I8):
 * base64url(SHA-256(JCS(operationHashInput(...)))). Matches the Rust core byte for byte (vectors).
 */
export function operationHash(mandateId, recipient, agentId, ctx) {
    return sha256b64(utf8(canonicalize(operationHashInput(mandateId, recipient, agentId, ctx))));
}
export function randomB64(bytes = 32) {
    return b64url.encode(crypto.getRandomValues(new Uint8Array(bytes)));
}
const MAX_SAFE = Number.MAX_SAFE_INTEGER;
/** RFC 8785 JCS, protocol-v1 profile (rejects non-integers and unsafe integers). */
export function canonicalize(value, depth = 0) {
    if (depth > 32)
        throw new Error('nesting too deep');
    if (value === null)
        return 'null';
    if (typeof value === 'boolean')
        return value ? 'true' : 'false';
    if (typeof value === 'number') {
        if (!Number.isInteger(value) || Math.abs(value) > MAX_SAFE)
            throw new Error('protocol v1 permits safe integers only');
        return String(value);
    }
    if (typeof value === 'string')
        return JSON.stringify(value);
    if (Array.isArray(value))
        return `[${value.map((v) => canonicalize(v, depth + 1)).join(',')}]`;
    if (typeof value === 'object') {
        const entries = Object.entries(value).filter(([, v]) => v !== undefined);
        entries.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)); // UTF-16 code unit order
        return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalize(v, depth + 1)}`).join(',')}}`;
    }
    throw new Error(`unsupported JSON value: ${typeof value}`);
}
export const ed25519 = {
    publicFromSecret(secretB64) {
        return b64url.encode(ed.getPublicKey(b64url.decode(secretB64)));
    },
    sign(secretB64, msg) {
        return b64url.encode(ed.sign(msg, b64url.decode(secretB64)));
    },
    verify(publicB64, msg, sigB64) {
        try {
            const pk = b64url.decode(publicB64);
            const sig = b64url.decode(sigB64);
            if (pk.length !== 32 || sig.length !== 64)
                return false;
            return ed.verify(sig, msg, pk, { zip215: false });
        }
        catch {
            return false;
        }
    },
    generate() {
        const sk = ed.utils.randomSecretKey();
        return { secretKey: b64url.encode(sk), publicKey: b64url.encode(ed.getPublicKey(sk)) };
    },
    fingerprint(publicB64) {
        return sha256b64(b64url.decode(publicB64)).slice(0, 22);
    },
};
export function jwsDecode(token) {
    const parts = token.split('.');
    if (parts.length !== 3)
        throw new Error('compact JWS must have 3 parts');
    return {
        header: JSON.parse(dec.decode(b64url.decode(parts[0]))),
        payload: JSON.parse(dec.decode(b64url.decode(parts[1]))),
    };
}
/** Verify a protocol-v1 JWS. Returns null on ANY failure (fail closed). */
export function jwsVerify(publicB64, token, expectedTyp) {
    try {
        const parts = token.split('.');
        if (parts.length !== 3)
            return null;
        const d = jwsDecode(token);
        if (d.header.alg !== 'EdDSA' || d.header.v !== PROTOCOL_VERSION || d.header.typ !== expectedTyp || typeof d.header.kid !== 'string')
            return null;
        if ('crit' in d.header)
            return null;
        if (!b64url.isCanonical(parts[2]))
            return null; // one encoding per signature (L15)
        if (!ed25519.verify(publicB64, utf8(`${parts[0]}.${parts[1]}`), parts[2]))
            return null;
        if (typeof d.payload !== 'object' || d.payload === null || Array.isArray(d.payload))
            return null;
        return d;
    }
    catch {
        return null;
    }
}
export function jwsSign(secretB64, kid, typ, payload) {
    const h = b64url.encode(utf8(canonicalize({ alg: 'EdDSA', kid, typ, v: PROTOCOL_VERSION })));
    const p = b64url.encode(utf8(canonicalize(payload)));
    return `${h}.${p}.${ed25519.sign(secretB64, utf8(`${h}.${p}`))}`;
}
export const contentDigest = (body) => `sha-256=${sha256b64(body)}`;
export function signatureBase(r) {
    const fields = [
        ['principal', r.principal], ['kid', r.keyId], ['method', r.method], ['audience', r.audience],
        ['target', r.target], ['content-digest', r.contentDigest], ['timestamp', r.timestamp], ['nonce', r.nonce],
    ];
    for (const [name, v] of fields) {
        // eslint-disable-next-line no-control-regex
        if (!v || v.length > 2048 || /[\u0000-\u001f\u007f-\u009f]/.test(v))
            throw new Error(`invalid request field ${name}`);
    }
    if (!/^[A-Z]+$/.test(r.method))
        throw new Error('method must be uppercase');
    if (!r.target.startsWith('/'))
        throw new Error('target must be origin-form');
    if (!r.contentDigest.startsWith('sha-256='))
        throw new Error('content digest must be sha-256');
    if (r.nonce.length < 16)
        throw new Error('nonce too short');
    return [REQUEST_SIG_VERSION, ...fields.map(([n, v]) => `${n}: ${v}`)].join('\n');
}
export const isoSeconds = (ms = Date.now()) => new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z');
